/* eslint-disable prettier/prettier */
import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import AchievementsSection from "components/About/AchievementsSection";
import { timeline, achievements } from "config/aboutData";
import PhotoSection from "components/About/PhotoSection";
// Framer Motion Animations
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.6 },
  }),
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2,
    },
  },
};

export default function About() {
  const navigate = useNavigate();

  return (
    <MKBox position="relative" py={6} px={3}>
      {/* Animated Background Blobs */}
      <MKBox
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        zIndex={-1}
        sx={{ overflow: "hidden" }}
      >
        <MKBox
          sx={{
            position: "absolute",
            top: "10%",
            left: "5%",
            width: "250px",
            height: "250px",
            borderRadius: "50%",
            filter: "blur(100px)",
            opacity: 0.2,
            backgroundColor: "#00f2ff",
            animation: "float 10s ease-in-out infinite",
          }}
        />
        <MKBox
          sx={{
            position: "absolute",
            bottom: "10%",
            right: "5%",
            width: "300px",
            height: "300px",
            borderRadius: "50%",
            filter: "blur(100px)",
            opacity: 0.2,
            backgroundColor: "#5e60ce",
            animation: "float 12s ease-in-out infinite",
          }}
        />
      </MKBox>

      {/* Overlay Container */}
      <MKBox
        px={4}
        py={6}
        borderRadius="xl"
        bgcolor="rgba(255, 255, 255, 0.75)"
        backdropFilter="blur(10px)"
        boxShadow="xl"
        maxWidth="1200px"
        mx="auto"
      >
        {/* Header */}
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <MKTypography
            variant="h2"
            fontWeight="bold"
            mb={4}
            textAlign="center"
            color="info.main"
          >
            About Matrix-180 Solutions
          </MKTypography>
        </motion.div>

        <motion.div initial="hidden" animate="visible" custom={1} variants={fadeUp}>
          <MKTypography
            variant="body1"
            color="text"
            textAlign="center"
            maxWidth="800px"
            mx="auto"
            mb={6}
          >
            Matrix-180 is a modern AI-driven solutions agency helping businesses grow smarter. We
            specialise in automation, data analytics, and seamless digital experiences tailored for
            modern businesses. Whether you are a startup or scaling enterprise, our mission is to
            deliver intelligent tools that drive results.
          </MKTypography>
        </motion.div>
        <PhotoSection />
        {/* Our Journey */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <MKTypography variant="h4" fontWeight="bold" mt={6} mb={4} textAlign="center">
            Our Journey
          </MKTypography>

          <MKBox display="flex" justifyContent="center">
            <MKBox
              sx={{
                borderLeft: "2px solid #1976d2",
                pl: 3,
                position: "relative",
                maxWidth: "600px",
              }}
            >
              {timeline.map((item, index) => (
                <motion.div
                  key={index}
                  custom={index}
                  initial={{ opacity: 0, x: -40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                >
                  <MKBox sx={{ position: "relative", mb: 4 }}>
                    {/* Dot */}
                    <MKBox
                      sx={{
                        position: "absolute",
                        left: "-16px",
                        top: "6px",
                        width: "12px",
                        height: "12px",
                        bgcolor: "info.main",
                        borderRadius: "50%",
                      }}
                    />
                    {/* Text */}
                    <MKTypography variant="body1">
                      <strong>{item.year}:</strong> {item.event}
                    </MKTypography>
                  </MKBox>
                </motion.div>
              ))}
            </MKBox>
          </MKBox>
        </motion.div>


        {/* Achievements */}
        <AchievementsSection data={achievements} />

        {/* CTA Section */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <MKTypography variant="h5" textAlign="center" mt={4}>
            Ready to optimise your operations? <br /> Contact us today to get started.
          </MKTypography>

          <MKBox textAlign="center" mt={3}>
            <MKButton
              variant="gradient"
              color="info"
              size="large"
              onClick={() => navigate("/contact")}
            >
              Contact Us
            </MKButton>
          </MKBox>
        </motion.div>
      </MKBox>
    </MKBox>
  );
}
